import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, 
  Shield, 
  Navigation2,
  Phone,
  Brain,
  Award,
  Zap
} from 'lucide-react';
import { Emergency, Responder, ChatMessage, ResponderSkill, BlockchainVerification } from './types';
import { EmergencyRequestForm } from './components/EmergencyRequestForm';
import { ResponderDashboard } from './components/ResponderDashboard';
import { EnhancedMatchingEngine } from './utils/enhancedMatchingEngine';
import { AIEmergencyEngine } from './utils/aiEngine';
import { BlockchainManager } from './utils/blockchainManager';

function App() {
  const [currentView, setCurrentView] = useState<'request' | 'dashboard'>('request');
  const [emergencies, setEmergencies] = useState<Emergency[]>([]);
  const [responders, setResponders] = useState<Responder[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const matchingEngine = new EnhancedMatchingEngine();
  const aiEngine = new AIEmergencyEngine();
  const blockchainManager = new BlockchainManager();

  // Initialize enhanced mock responders with AI and blockchain features
  useEffect(() => {
    const initializeResponders = async () => {
      const mockResponders: Responder[] = [
        {
          id: '1',
          name: 'Fire Station 1 - Unit Alpha',
          category: 'fire',
          location: { latitude: 40.7589, longitude: -73.9851, address: '123 Fire St, NYC' },
          status: 'available',
          availability: 'available',
          isAvailable: true,
          phone: '+1-555-FIRE-01',
          badgeNumber: 'FS1-ALPHA',
          isOnline: true,
          lastLocationUpdate: new Date(),
          skills: [
            { name: 'Firefighting', level: 'expert', certificationDate: new Date('2020-01-15') },
            { name: 'Hazmat Response', level: 'advanced', certificationDate: new Date('2021-03-10') },
            { name: 'Technical Rescue', level: 'intermediate', certificationDate: new Date('2022-06-20') }
          ],
          performanceMetrics: {
            totalResponses: 156,
            averageResponseTime: 8.5,
            successRate: 0.94,
            rating: 4.8,
            specializations: ['structural fires', 'hazmat incidents', 'vehicle extrication']
          },
          blockchainVerification: {
            walletAddress: 'ALGO1234567890ABCDEF',
            verificationStatus: 'verified',
            transactionId: 'TXN123456789',
            verificationDate: new Date('2024-01-15'),
            credentialHash: 'hash123abc456def'
          },
          certifications: [
            {
              id: 'cert1',
              name: 'Fire Officer Certification',
              issuer: 'National Fire Academy',
              issueDate: new Date('2020-01-15'),
              expiryDate: new Date('2025-01-15'),
              verified: true,
              blockchainHash: 'cert_hash_123'
            }
          ]
        },
        {
          id: '2',
          name: 'Dr. Sarah Mitchell',
          category: 'medical',
          location: { latitude: 40.7505, longitude: -73.9934, address: '456 Medical Plaza, NYC' },
          status: 'available',
          availability: 'available',
          isAvailable: true,
          phone: '+1-555-MED-123',
          badgeNumber: 'MD-2487',
          isOnline: true,
          lastLocationUpdate: new Date(),
          skills: [
            { name: 'Emergency Medicine', level: 'expert', certificationDate: new Date('2018-05-20') },
            { name: 'Trauma Surgery', level: 'expert', certificationDate: new Date('2019-08-15') },
            { name: 'Pediatric Care', level: 'advanced', certificationDate: new Date('2020-11-30') }
          ],
          performanceMetrics: {
            totalResponses: 203,
            averageResponseTime: 12.3,
            successRate: 0.97,
            rating: 4.9,
            specializations: ['cardiac emergencies', 'trauma care', 'pediatric medicine']
          },
          blockchainVerification: {
            walletAddress: 'ALGO9876543210FEDCBA',
            verificationStatus: 'verified',
            transactionId: 'TXN987654321',
            verificationDate: new Date('2024-01-10'),
            credentialHash: 'hash456def789ghi'
          },
          certifications: [
            {
              id: 'cert2',
              name: 'Board Certification in Emergency Medicine',
              issuer: 'American Board of Emergency Medicine',
              issueDate: new Date('2018-05-20'),
              expiryDate: new Date('2028-05-20'),
              verified: true,
              blockchainHash: 'cert_hash_456'
            }
          ]
        },
        {
          id: '3',
          name: 'Officer Johnson',
          category: 'police',
          location: { latitude: 40.7614, longitude: -73.9776, address: '789 Police Plaza, NYC' },
          status: 'available',
          availability: 'available',
          isAvailable: true,
          phone: '+1-555-POLICE',
          badgeNumber: 'PD-5521',
          isOnline: true,
          lastLocationUpdate: new Date(),
          skills: [
            { name: 'Law Enforcement', level: 'expert', certificationDate: new Date('2015-09-10') },
            { name: 'Crisis Intervention', level: 'advanced', certificationDate: new Date('2020-04-25') },
            { name: 'Traffic Control', level: 'expert', certificationDate: new Date('2017-12-05') }
          ],
          performanceMetrics: {
            totalResponses: 89,
            averageResponseTime: 6.8,
            successRate: 0.91,
            rating: 4.6,
            specializations: ['domestic violence', 'traffic incidents', 'crowd control']
          },
          blockchainVerification: {
            walletAddress: 'ALGO5555666677778888',
            verificationStatus: 'pending',
            transactionId: undefined,
            verificationDate: undefined,
            credentialHash: undefined
          },
          certifications: [
            {
              id: 'cert3',
              name: 'Police Officer Certification',
              issuer: 'State Police Academy',
              issueDate: new Date('2015-09-10'),
              expiryDate: new Date('2025-09-10'),
              verified: false
            }
          ]
        },
        {
          id: '4',
          name: 'Rescue Team Beta',
          category: 'rescue',
          location: { latitude: 40.7282, longitude: -74.0776, address: '321 Rescue Ave, NYC' },
          status: 'available',
          availability: 'available',
          isAvailable: true,
          phone: '+1-555-RESCUE',
          badgeNumber: 'RT-BETA',
          isOnline: true,
          lastLocationUpdate: new Date(),
          skills: [
            { name: 'Technical Rescue', level: 'expert', certificationDate: new Date('2019-03-15') },
            { name: 'Water Rescue', level: 'advanced', certificationDate: new Date('2020-07-20') },
            { name: 'Rope Access', level: 'expert', certificationDate: new Date('2018-11-10') }
          ],
          performanceMetrics: {
            totalResponses: 67,
            averageResponseTime: 15.2,
            successRate: 0.89,
            rating: 4.7,
            specializations: ['confined space rescue', 'high angle rescue', 'water rescue']
          },
          blockchainVerification: {
            walletAddress: 'ALGO1111222233334444',
            verificationStatus: 'verified',
            transactionId: 'TXN111222333',
            verificationDate: new Date('2024-01-20'),
            credentialHash: 'hash789ghi012jkl'
          },
          certifications: [
            {
              id: 'cert4',
              name: 'Technical Rescue Certification',
              issuer: 'International Association of Fire Chiefs',
              issueDate: new Date('2019-03-15'),
              expiryDate: new Date('2024-03-15'),
              verified: true,
              blockchainHash: 'cert_hash_789'
            }
          ]
        }
      ];

      setResponders(mockResponders);
    };

    initializeResponders();
  }, []);

  // Request notification permission on app load
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const handleEmergencySubmit = async (emergencyData: Omit<Emergency, 'id' | 'status' | 'timestamp'>) => {
    setIsLoading(true);

    try {
      // Create new emergency with AI classification
      const aiClassification = await aiEngine.classifyEmergency(emergencyData.description);
      
      const newEmergency: Emergency = {
        ...emergencyData,
        id: Date.now().toString(),
        status: 'pending',
        timestamp: new Date(),
        chatMessages: [],
        aiClassification: {
          confidence: aiClassification.confidence,
          suggestedType: aiClassification.type as any,
          suggestedSeverity: aiClassification.severity as any,
          keywords: aiClassification.keywords,
          riskFactors: aiClassification.riskFactors,
          recommendedActions: aiClassification.recommendedActions
        },
        // Override with AI suggestions if confidence is high
        type: aiClassification.confidence > 0.8 ? aiClassification.type as any : emergencyData.type,
        severity: aiClassification.confidence > 0.8 ? aiClassification.severity as any : emergencyData.severity
      };

      // Add the emergency to the list
      setEmergencies(prev => [newEmergency, ...prev]);

      // Try to find and assign optimal responder using enhanced AI matching
      const matchResult = await matchingEngine.findOptimalResponder(newEmergency, responders);
      
      if (matchResult) {
        // Update emergency with assigned responder and AI routing data
        const assignedEmergency: Emergency = {
          ...newEmergency,
          status: 'assigned',
          assignedResponder: matchResult.responderId,
          estimatedArrival: new Date(Date.now() + matchResult.estimatedArrival * 60000),
          routingData: {
            estimatedTravelTime: matchResult.estimatedArrival,
            trafficConditions: 'moderate',
            weatherImpact: 'none',
            alternativeRoutes: 2,
            optimalRoute: [
              responders.find(r => r.id === matchResult.responderId)?.location || { latitude: 0, longitude: 0 },
              newEmergency.location
            ]
          }
        };

        // Update responder status
        setResponders(prev => prev.map(responder => 
          responder.id === matchResult.responderId
            ? { 
                ...responder, 
                status: 'en-route', 
                availability: 'busy',
                isAvailable: false 
              }
            : responder
        ));

        // Update emergency list
        setEmergencies(prev => prev.map(emergency => 
          emergency.id === newEmergency.id ? assignedEmergency : emergency
        ));

        // Log emergency response on blockchain
        const assignedResponder = responders.find(r => r.id === matchResult.responderId);
        if (assignedResponder) {
          await blockchainManager.logEmergencyResponse({
            id: assignedEmergency.id,
            type: assignedEmergency.type,
            severity: assignedEmergency.severity,
            responderId: matchResult.responderId,
            responseTime: matchResult.estimatedArrival,
            resolution: 'assigned'
          });
        }

        // Show enhanced confirmation with AI insights
        const aiInsights = aiClassification.recommendedActions.slice(0, 3).join(', ');
        alert(`Emergency reported successfully!\n\nAI Classification: ${aiClassification.type} (${Math.round(aiClassification.confidence * 100)}% confidence)\nAssigned: ${assignedResponder?.name}\nEstimated arrival: ${matchResult.estimatedArrival} minutes\nAI Recommendations: ${aiInsights}\n\nEmergency services have been notified and logged on blockchain.`);
        
        // Show browser notification
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('AI-Enhanced Emergency Response Assigned', {
            body: `${assignedResponder?.name} is responding to your ${aiClassification.type} emergency`,
            icon: '/emergency-icon.png'
          });
        }
      } else {
        alert(`Emergency reported successfully!\n\nAI Classification: ${aiClassification.type} (${Math.round(aiClassification.confidence * 100)}% confidence)\nNo verified responders are currently available. Your request has been queued and will be assigned as soon as a responder becomes available.\n\nAI Recommendations: ${aiClassification.recommendedActions.slice(0, 2).join(', ')}`);
      }

      // Switch to dashboard view
      setCurrentView('dashboard');

    } catch (error) {
      console.error('Failed to process emergency:', error);
      alert('Failed to process emergency request. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateEmergencyStatus = async (emergencyId: string, newStatus: Emergency['status']) => {
    setEmergencies(prev => prev.map(emergency => {
      if (emergency.id === emergencyId) {
        const updatedEmergency = { 
          ...emergency, 
          status: newStatus,
          resolutionTime: newStatus === 'resolved' ? 
            Math.round((Date.now() - emergency.timestamp.getTime()) / 60000) : undefined
        };
        
        // If resolving, make the responder available again and log on blockchain
        if (newStatus === 'resolved' && emergency.assignedResponder) {
          setResponders(prevResponders => prevResponders.map(responder => 
            responder.id === emergency.assignedResponder
              ? { 
                  ...responder, 
                  status: 'available', 
                  availability: 'available',
                  isAvailable: true 
                }
              : responder
          ));

          // Log resolution on blockchain
          blockchainManager.logEmergencyResponse({
            id: emergencyId,
            type: emergency.type,
            severity: emergency.severity,
            responderId: emergency.assignedResponder,
            responseTime: updatedEmergency.resolutionTime || 0,
            resolution: 'resolved'
          });
        }
        
        return updatedEmergency;
      }
      return emergency;
    }));
  };

  const handleUpdateResponderAvailability = (responderId: string, availability: Responder['availability']) => {
    setResponders(prev => prev.map(responder => 
      responder.id === responderId
        ? { 
            ...responder, 
            availability,
            isAvailable: availability === 'available',
            status: availability === 'available' ? 'available' : responder.status
          }
        : responder
    ));
  };

  // Enhanced real-time updates with AI pattern recognition
  useEffect(() => {
    const interval = setInterval(async () => {
      // Occasionally update responder locations (simulating movement)
      setResponders(prev => prev.map(responder => {
        if (Math.random() < 0.1) { // 10% chance to update location
          return {
            ...responder,
            location: {
              ...responder.location,
              latitude: responder.location.latitude + (Math.random() - 0.5) * 0.01,
              longitude: responder.location.longitude + (Math.random() - 0.5) * 0.01,
            },
            lastLocationUpdate: new Date(),
          };
        }
        return responder;
      }));

      // AI-powered emergency assignment for pending emergencies
      const pendingEmergencies = emergencies.filter(e => e.status === 'pending');
      for (const emergency of pendingEmergencies) {
        const availableResponders = responders.filter(r => 
          r.availability === 'available' && 
          r.blockchainVerification?.verificationStatus === 'verified'
        );
        
        if (availableResponders.length > 0) {
          const matchResult = await matchingEngine.findOptimalResponder(emergency, availableResponders);
          if (matchResult) {
            // Update responder status
            setResponders(prevResponders => prevResponders.map(responder => 
              responder.id === matchResult.responderId
                ? { 
                    ...responder, 
                    status: 'en-route', 
                    availability: 'busy',
                    isAvailable: false 
                  }
                : responder
            ));

            // Update emergency
            setEmergencies(prevEmergencies => prevEmergencies.map(e => 
              e.id === emergency.id ? {
                ...e,
                status: 'assigned',
                assignedResponder: matchResult.responderId,
                estimatedArrival: new Date(Date.now() + matchResult.estimatedArrival * 60000),
              } : e
            ));

            // Log on blockchain
            await blockchainManager.logEmergencyResponse({
              id: emergency.id,
              type: emergency.type,
              severity: emergency.severity,
              responderId: matchResult.responderId,
              responseTime: matchResult.estimatedArrival,
              resolution: 'auto-assigned'
            });
          }
        }
      }
    }, 5000); // Update every 5 seconds

    return () => clearInterval(interval);
  }, [responders, emergencies]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Enhanced Navigation with Built with Bolt Badge */}
      <nav className="bg-white shadow-2xl border-b-4 border-red-600">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center h-24">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-10 h-10 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Emergency Response Coordinator</h1>
                <div className="flex items-center gap-8 mt-2">
                  <div className="flex items-center gap-2">
                    <Brain className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold text-lg text-gray-700">AI-Powered</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-purple-600" />
                    <span className="font-semibold text-lg text-gray-700">Blockchain Verified</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              {/* Built with Bolt Badge */}
              <a 
                href="https://bolt.new/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full font-bold text-lg shadow-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105"
              >
                <Zap className="w-6 h-6" />
                ⚡ Built with Bolt
              </a>
              
              <div className="hidden sm:flex items-center gap-3 text-red-600 font-bold text-lg">
                <Phone className="w-5 h-5" />
                <span>Emergency: 911</span>
              </div>
              
              <div className="flex rounded-xl overflow-hidden border-4 border-gray-300">
                <button
                  onClick={() => setCurrentView('request')}
                  disabled={isLoading}
                  className={`px-6 py-3 text-lg font-bold transition-colors disabled:opacity-50 ${
                    currentView === 'request'
                      ? 'bg-red-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <AlertTriangle className="w-5 h-5 inline mr-2" />
                  Report Emergency
                </button>
                <button
                  onClick={() => setCurrentView('dashboard')}
                  disabled={isLoading}
                  className={`px-6 py-3 text-lg font-bold transition-colors disabled:opacity-50 ${
                    currentView === 'dashboard'
                      ? 'bg-red-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Shield className="w-5 h-5 inline mr-2" />
                  Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {currentView === 'request' ? (
          <div className="max-w-4xl mx-auto">
            <EmergencyRequestForm onSubmit={handleEmergencySubmit} />
            {isLoading && (
              <div className="mt-8 p-8 bg-blue-50 rounded-2xl border-4 border-blue-200">
                <div className="flex items-center gap-4">
                  <Brain className="w-10 h-10 text-blue-600 animate-pulse" />
                  <div>
                    <p className="font-bold text-blue-900 text-2xl">Processing with AI...</p>
                    <p className="text-lg text-blue-700">Analyzing emergency, finding optimal responder, and logging on blockchain</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <ResponderDashboard
            emergencies={emergencies}
            responders={responders}
            onUpdateEmergencyStatus={handleUpdateEmergencyStatus}
            onUpdateResponderAvailability={handleUpdateResponderAvailability}
          />
        )}
      </main>

      {/* Enhanced Emergency Contact Footer */}
      <footer className="bg-red-600 text-white py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-3 text-2xl font-bold mb-4">
            <Phone className="w-8 h-8" />
            <span>For Life-Threatening Emergencies: Call 911 Immediately</span>
          </div>
          <p className="text-red-100 text-lg mb-4">
            This system is for emergency coordination. In case of immediate danger, always call 911 first.
          </p>
          <div className="flex items-center justify-center gap-8 text-sm text-red-200">
            <div className="flex items-center gap-2">
              <Brain className="w-4 h-4" />
              <span>AI-Enhanced Emergency Classification</span>
            </div>
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4" />
              <span>Blockchain-Verified Responders</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span>Algorand Testnet Integration</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;